// DAS-2181: Terminal Layout — shell component for the terminal dashboard view
import React, { useState, useCallback, useEffect } from 'react';
import { terminalZoneConfig } from './terminalZoneConfig';
import TerminalHero from './TerminalHero';
import TerminalPanels from './TerminalPanels';
import TerminalDataSection from './TerminalDataSection';
import TerminalAnnotation from './TerminalAnnotation';
import api from '../functions/api';
import { weatherExplanation } from './MarketWeatherBadge'; // DAS-2765
import './terminal.css';

const formatTime = () => {
  const now = new Date();
  const days = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
  const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
  const hh = String(now.getHours()).padStart(2, '0');
  const mm = String(now.getMinutes()).padStart(2, '0');
  return `${days[now.getDay()]} ${now.getDate()} ${months[now.getMonth()]} ${now.getFullYear()} \u00B7 ${hh}:${mm} CET`;
};

const syncAnnotationsToFile = (annotations) => {
  api.post('/design_annotations', { annotations })
    .catch(() => {});
};

const TerminalLayout = ({ cards, onCardClick, onViewModeChange, data = [], source, alertCount = 0, panelData, tpData, yfData, exposures, benchmarkData, stockTickers, portfolioRisk }) => {
  const [annotationMode, setAnnotationMode] = useState(false);
  const [annotationTarget, setAnnotationTarget] = useState(null);
  // DAS-2765: daily market weather flag (CALM / NERVOUS / PANIC)
  const [marketWeather, setMarketWeather] = useState(null);

  useEffect(() => {
    api.get('/market_weather')
      .then(res => setMarketWeather(res.data?.current || null))
      .catch(() => setMarketWeather(null));
  }, []);
  const [annotations, setAnnotations] = useState(() => {
    try {
      const stored = localStorage.getItem('terminal_annotations');
      return stored ? JSON.parse(stored).annotations || [] : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    api.get('/design_annotations')
      .then(res => {
        const serverAnns = res.data?.annotations || [];
        setAnnotations(prev => {
          const localIds = new Set(prev.map(a => a.id));
          const serverIds = new Set(serverAnns.map(a => a.id));
          const merged = [
            ...prev,
            ...serverAnns.filter(a => !localIds.has(a.id)),
          ];
          const serverMissing = prev.some(a => !serverIds.has(a.id));
          localStorage.setItem('terminal_annotations', JSON.stringify({ version: 1, annotations: merged }));
          if (serverMissing || merged.length > serverAnns.length) {
            syncAnnotationsToFile(merged);
          }
          return merged;
        });
      })
      .catch(() => {});
  }, []);

  const handleAnnotationClick = useCallback((regionId, regionLabel) => {
    setAnnotationTarget({ regionId, regionLabel });
  }, []);

  const handleAnnotationSave = useCallback((regionId, text) => {
    const newAnnotation = {
      id: `ann-${Date.now()}`,
      region: regionId,
      regionLabel: annotationTarget?.regionLabel || regionId,
      text,
      timestamp: new Date().toISOString(),
      status: 'open',
    };
    setAnnotations(prev => {
      const updated = [...prev, newAnnotation];
      localStorage.setItem('terminal_annotations', JSON.stringify({ version: 1, annotations: updated }));
      syncAnnotationsToFile(updated);
      return updated;
    });
    setAnnotationTarget(null);
  }, [annotationTarget]);

  const handleAnnotationCancel = useCallback(() => {
    setAnnotationTarget(null);
  }, []);

  const positionCount = data.length;
  const categories = [...new Set(data.map(d => d.Category).filter(Boolean))].length;

  // Regime from volatility
  const vol = portfolioRisk?.annualized_volatility;
  const regime = vol != null
    ? (vol > 25 ? { label: 'HIGH VOL', cls: 'dn' } : vol > 15 ? { label: 'NEUTRAL', cls: 'warn' } : { label: 'LOW VOL', cls: 'up' })
    : null;

  return (
    <div className="terminal-root">
      {/* Top Bar */}
      <div className="topbar">
        <span className="tb-brand">Portfolio Terminal</span>

        {terminalZoneConfig.tabs.map((tab) => (
          <div className={`tb-tab${tab.active ? ' on' : ''}`} key={tab.id}>
            {tab.label}
            {tab.showBadge && alertCount > 0 && (
              <span className="alert-badge">{alertCount}</span>
            )}
          </div>
        ))}

        <div className="tb-right">
          <div className="tb-toggle">
            <button className="tg active">terminal</button>
            <button className="tg" onClick={() => onViewModeChange?.('cards')}>cards</button>
          </div>
          <div className="tb-sep" />
          <button
            className={`annotation-toggle-btn${annotationMode ? ' active' : ''}`}
            onClick={() => {
              setAnnotationMode(m => !m);
              setAnnotationTarget(null);
            }}
          >
            {annotationMode ? 'exit annotate' : 'annotate'}
          </button>
          <span className="tb-time">{formatTime()}</span>
        </div>
      </div>

      {/* Band 1: Hero Strip */}
      <TerminalHero
        cards={cards}
        heroConfig={terminalZoneConfig.hero}
        onCardClick={onCardClick}
        annotationMode={annotationMode}
        onAnnotationClick={handleAnnotationClick}
        annotations={annotations}
      />

      {/* Band 2: Analysis Panels */}
      <TerminalPanels
        panelData={panelData}
        onCardClick={onCardClick}
        annotationMode={annotationMode}
        onAnnotationClick={handleAnnotationClick}
        annotations={annotations}
      />

      {/* Band 3: Data Section — Positions table + News + Performance */}
      <TerminalDataSection
        data={data}
        exposures={exposures}
        tpData={tpData}
        yfData={yfData}
        benchmarkData={benchmarkData}
        stockTickers={stockTickers}
        onCardClick={onCardClick}
        annotationMode={annotationMode}
        onAnnotationClick={handleAnnotationClick}
        annotations={annotations}
        source={source}
      />

      {/* Status Bar */}
      <div className="statusbar">
        <span className="sb-item">
          <span className={`sb-dot${source === 'portfolio' ? ' live' : ''}`} />
          {source === 'portfolio' ? 'live' : 'snapshot'}
        </span>
        <span className="sb-item">
          {positionCount} positions &middot; <span>{categories} categories</span>
        </span>
        <span className="sb-item">
          data: <span>{source === 'portfolio' ? 'saxo bank \u00B7 yfinance \u00B7 trendsearch' : 'file drop \u00B7 yfinance'}</span>
        </span>
        {regime && (
          <span className="sb-item">
            regime: <span className={regime.cls}>{regime.label}</span>
          </span>
        )}
        {marketWeather && (
          <span
            className="sb-item"
            data-testid="market-weather-flag"
            title={weatherExplanation(marketWeather)}
          >
            weather: <span className={marketWeather.weather === 'CALM' ? 'up' : marketWeather.weather === 'NERVOUS' ? 'warn' : 'dn'}>
              {marketWeather.weather}
            </span>
          </span>
        )}
        {annotations.filter(a => a.status === 'open').length > 0 && (
          <span className="sb-item sb-annotations">
            {annotations.filter(a => a.status === 'open').length} open annotations
          </span>
        )}
        <span style={{ marginLeft: 'auto' }} className="sb-item">
          monosoft v2
        </span>
      </div>

      {/* Annotation overlay input */}
      {annotationMode && annotationTarget && (
        <TerminalAnnotation
          target={annotationTarget}
          annotations={annotations.filter(a => a.region === annotationTarget.regionId)}
          onSave={(text) => handleAnnotationSave(annotationTarget.regionId, text)}
          onCancel={handleAnnotationCancel}
        />
      )}
    </div>
  );
};

export default TerminalLayout;
